"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.useMeta = void 0;
const interface_1 = require("../interface");
const react_1 = require("react");
const react_redux_1 = require("react-redux");
const store_1 = require("../store");
const context_1 = require("../context");
const getInstallPosition = (state) => {
    var _a, _b, _c;
    if ((_a = state.pageParams) === null || _a === void 0 ? void 0 : _a.dashboardId) {
        return interface_1.InstallPosition.Dashboard;
    }
    if (((_b = state.pageParams) === null || _b === void 0 ? void 0 : _b.mirrorId) || ((_c = state.pageParams) === null || _c === void 0 ? void 0 : _c.datasheetId)) {
        return interface_1.InstallPosition.WidgetPanel;
    }
    return undefined;
};
/**
 * Get meta information of the widget, including the widget itself, the author, the information in datasheet, etc.
 *
 * @returns
 *
 * ### Example
 * ```js
 * import { useMeta } from '@apitable/widget-sdk';
 *
 * // Show the datasheet and author associated with the widget
 * function Meta() {
 *   const { datasheetName, authorName } = useMeta();
 *   return (<div>
 *     <p>Name of the datasheet associated with the widget: {datasheetName}</p>
 *     <p>Widget author: {authorName}</p>
 *   </div>);
 * }
 * ```
 *
 */
function useMeta() {
    const { id, theme, runtimeEnv = interface_1.RuntimeEnv.Desktop } = (0, react_1.useContext)(context_1.WidgetContext);
    const metaData = (0, react_redux_1.useSelector)(state => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
        const datasheet = (0, store_1.getWidgetDatasheet)(state);
        return {
            name: (_a = state.widget) === null || _a === void 0 ? void 0 : _a.snapshot.widgetName,
            widgetPackageIcon: (_b = state.widget) === null || _b === void 0 ? void 0 : _b.widgetPackageIcon,
            widgetPackageName: (_c = state.widget) === null || _c === void 0 ? void 0 : _c.widgetPackageName,
            widgetPackageVersion: (_d = state.widget) === null || _d === void 0 ? void 0 : _d.widgetPackageVersion,
            // Compatibility audit widget ID, in order to smoothly load the widget code
            widgetPackageId: ((_e = state.widget) === null || _e === void 0 ? void 0 : _e.fatherWidgetPackageId) || ((_f = state.widget) === null || _f === void 0 ? void 0 : _f.widgetPackageId),
            authorEmail: (_g = state.widget) === null || _g === void 0 ? void 0 : _g.authorEmail,
            authorIcon: (_h = state.widget) === null || _h === void 0 ? void 0 : _h.authorIcon,
            authorLink: (_j = state.widget) === null || _j === void 0 ? void 0 : _j.authorLink,
            authorName: (_k = state.widget) === null || _k === void 0 ? void 0 : _k.authorName,
            packageType: (_l = state.widget) === null || _l === void 0 ? void 0 : _l.packageType,
            releaseType: (_m = state.widget) === null || _m === void 0 ? void 0 : _m.releaseType,
            releaseCodeBundle: (_o = state.widget) === null || _o === void 0 ? void 0 : _o.releaseCodeBundle,
            datasheetId: datasheet === null || datasheet === void 0 ? void 0 : datasheet.datasheetId,
            datasheetName: datasheet === null || datasheet === void 0 ? void 0 : datasheet.datasheetName,
            status: (_p = state.widget) === null || _p === void 0 ? void 0 : _p.status,
            widgetId: (_q = state.widget) === null || _q === void 0 ? void 0 : _q.id,
            sourceId: (_r = state.widget) === null || _r === void 0 ? void 0 : _r.snapshot.sourceId,
            installPosition: getInstallPosition(state),
            spaceId: (_s = state.user) === null || _s === void 0 ? void 0 : _s.spaceId,
            theme,
            runtimeEnv,
        };
    }, react_redux_1.shallowEqual);
    return (0, react_1.useMemo)(() => {
        return Object.assign({ id }, metaData);
    }, [id, metaData]);
}
exports.useMeta = useMeta;
